/**
 * @module AdapterList
 * @package @geenius/adapters/solidjs
 * @description Renders the searchable adapter grid for the SolidJS variant.
 * The list consumes provider status from context and delegates selection
 * events to the hosting page or dashboard shell.
 */

import type { AdapterDomain, AdapterStatusType } from "@geenius/adapters";
import { ADAPTER_DOMAINS, DOMAIN_LABELS } from "@geenius/adapters";
import type { JSX } from "solid-js";
import { createMemo, createSignal, For, Show } from "solid-js";
import { cn } from "../lib/utils";
import { createAdapters } from "../primitives";
import { AdapterCard } from "./AdapterCard";

/**
 * Props for rendering the SolidJS adapter list and optional status filter controls.
 *
 * @property onSelect Optional callback invoked when a domain card is selected.
 * @property filterStatus Optional status filter applied before rendering the grid.
 * @property class Optional class name merged into the outer wrapper.
 */
export interface AdapterListProps {
  onSelect?: (domain: AdapterDomain) => void;
  filterStatus?: AdapterStatusType;
  class?: string;
}

/**
 * Renders the searchable SolidJS adapter list backed by shared provider state.
 *
 * @param props List props including selection callback, status filtering, and wrapper class names.
 * @returns The adapter grid or an empty state when no entries are available.
 */
export function AdapterList(props: AdapterListProps): JSX.Element {
  const ctx = createAdapters();
  const [search, setSearch] = createSignal("");

  const filtered = createMemo(() => {
    let domains = [...ADAPTER_DOMAINS];
    const query = search().toLowerCase();

    if (query) {
      domains = domains.filter((domain) => {
        return (
          (DOMAIN_LABELS[domain] ?? domain).toLowerCase().includes(query) ||
          domain.includes(query) ||
          (ctx.statuses[domain]?.provider?.toLowerCase().includes(query) ??
            false)
        );
      });
    }

    if (props.filterStatus) {
      domains = domains.filter(
        (domain) => ctx.statuses[domain]?.status === props.filterStatus,
      );
    }

    return domains;
  });

  return (
    <Show
      when={!ctx.isLoading}
      fallback={
        <div
          class={cn("grid gap-3", props.class)}
          role="status"
          aria-label="Loading adapters"
        >
          <For each={[1, 2, 3, 4]}>
            {() => (
              <div class="rounded-lg border border-border bg-card p-5 shadow-sm">
                <div class="flex items-start gap-4">
                  <div class="h-12 w-12 rounded-md border border-border bg-muted" />
                  <div class="flex-1 space-y-3 pt-1">
                    <div class="h-3 w-24 rounded-md bg-muted" />
                    <div class="h-2 w-48 rounded-md bg-muted/70" />
                  </div>
                </div>
              </div>
            )}
          </For>
        </div>
      }
    >
      <div class={props.class}>
        <div class="mb-4">
          <input
            type="text"
            value={search()}
            onInput={(event) => setSearch(event.currentTarget.value)}
            placeholder="Search adapters..."
            aria-label="Search adapters"
            class="w-full rounded-md border border-input bg-background px-4 py-3 text-sm text-foreground shadow-sm outline-none transition-colors motion-reduce:transition-none placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </div>

        <Show
          when={filtered().length > 0}
          fallback={
            <div class="rounded-lg border border-border bg-muted/40 p-8 text-center">
              <p class="text-sm font-medium text-muted-foreground">
                {search()
                  ? "No adapters match your search"
                  : "No adapters configured"}
              </p>
              <p class="mx-auto mt-2 max-w-sm text-xs text-muted-foreground">
                {search()
                  ? "Clear the search to return to the full adapter list."
                  : "Check the current filters or configure an adapter from the overview page."}
              </p>
              <Show when={search()}>
                <button
                  class="mt-4 rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary/30 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
                  onClick={() => setSearch("")}
                  type="button"
                >
                  Clear search
                </button>
              </Show>
            </div>
          }
        >
          <ul
            class="m-0 grid list-none gap-3 p-0 sm:grid-cols-2"
            aria-label="Adapter list"
          >
            <For each={filtered()}>
              {(domain) => (
                <li>
                  <AdapterCard
                    domain={domain}
                    status={ctx.statuses[domain]}
                    onClick={
                      props.onSelect
                        ? () => props.onSelect?.(domain)
                        : undefined
                    }
                  />
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>
    </Show>
  );
}
